// services/externalTransferService.ts
// Service for handling user transfers from external onboarding services
// SIMPLIFIED VERSION: External service writes directly to Firestore
// No API calls needed - profile already exists with data!

import { auth } from '../config/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { PrivateProfileService } from './privateProfileService';
import { PrivateProfile } from '../models/PrivateProfile';
import { loggingService } from './loggingService';
import { externalTransferStarted, errorOccurred, userContext } from '../utils/logging/actions';

export interface TransferResult {
  success: boolean;
  userId?: string;
  profile?: PrivateProfile;
  error?: string;
}

export class ExternalTransferService {
  /**
   * Main transfer handler
   * SIMPLIFIED: External service already wrote data to Firestore
   * We just need to authenticate and verify profile exists
   */
  static async handleTransfer(token: string): Promise<TransferResult> {
    const startTime = Date.now();
    
    try {
      // Log transfer started
      loggingService.log(externalTransferStarted('external_onboarding'));
      
      // Step 1: Authenticate with custom token
      let userCredential;
      try {
        userCredential = await signInWithCustomToken(auth, token);
      } catch (authError: any) {
        // Log authentication error
        loggingService.log(errorOccurred(
          authError.message || 'Custom token authentication failed',
          'external_transfer_auth_failed',
          {
            errorCode: authError.code || 'unknown',
            errorName: authError.name,
          }
        ));
        throw authError;
      }
      
      const user = userCredential.user;
      if (!user) {
        loggingService.log(errorOccurred(
          'No user returned after authentication',
          'external_transfer_no_user',
          {}
        ));
        throw new Error('Authentication failed');
      }

      // Step 2: Get profile (should already exist from external service)
      let profileResult;
      try {
        profileResult = await PrivateProfileService.getProfile(user.uid);
      } catch (profileError: any) {
        loggingService.log(errorOccurred(
          profileError.message || 'Profile load failed',
          'external_transfer_profile_load_failed',
          {
            userId: user.uid,
            errorCode: profileError.code || 'unknown',
          }
        ));
        throw profileError;
      }
      
      if (!profileResult.success || !profileResult.data) {
        // Profile doesn't exist - external service didn't create it
        loggingService.log(errorOccurred(
          'Profile not found in Firestore',
          'external_transfer_missing_profile',
          {
            userId: user.uid,
            email: user.email || '',
          }
        ));
        
        const createResult = await PrivateProfileService.ensureProfileExists(user.uid, {
          email: user.email || undefined,
          displayName: user.displayName || undefined,
        });
        
        return {
          success: true,
          userId: user.uid,
          profile: createResult.data,
        };
      }
      
      // Step 3: Verify profile has onboarding data
      const hasOnboardingData = profileResult.data.onboardingAnswers?.answers?.length > 0;
      const hasCalculationResults = !!profileResult.data.calculationResults;
      
      if (!hasOnboardingData) {
        loggingService.log(errorOccurred(
          'Profile exists but missing onboarding answers',
          'external_transfer_missing_onboarding',
          {
            userId: user.uid,
            hasCalculationResults,
          }
        ));
      }
      
      // Log successful transfer with comprehensive context
      const duration = Date.now() - startTime;
      loggingService.log(userContext({
        transferSuccess: true,
        userId: user.uid,
        onboardingAnswersCount: profileResult.data.onboardingAnswers?.answers?.length || 0,
        hasCalculationResults,
        transferDuration: duration,
        hasStructuredProfile: (profileResult.data.coreProfile?.goals?.length || 0) > 0,
      }));

      return {
        success: true,
        userId: user.uid,
        profile: profileResult.data,
      };
    } catch (error: any) {
      // Log final error
      const duration = Date.now() - startTime;
      loggingService.log(errorOccurred(
        error.message || 'Transfer failed',
        'external_transfer_failed',
        {
          errorCode: error.code || 'unknown',
          errorType: error.name || 'Error',
          duration,
        }
      ));
      
      // Provide user-friendly error messages
      let errorMessage = 'Transfer failed. Please try again.';
      
      if (error.code === 'auth/invalid-custom-token') {
        errorMessage = 'Invalid or expired transfer link. Please request a new link.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Helper to check if profile has been imported from external service
   */
  static hasExternalData(profile: PrivateProfile): boolean {
    return (
      profile.activeContext?.source === 'external_import' ||
      profile.coreProfile?.source === 'external_import' ||
      (profile.onboardingAnswers?.answers?.length || 0) > 0
    );
  }

  /**
   * Generate welcome summary from profile data
   * If structured data is missing, AI will extract it from onboarding Q&A
   */
  static generateWelcomeSummary(profile: PrivateProfile): string {
    const parts: string[] = [];
    
    // Try to use pre-extracted data if available
    if (profile.coreProfile?.goals && profile.coreProfile.goals.length > 0) {
      parts.push(`**Your Goals:** ${profile.coreProfile.goals.slice(0, 3).join(', ')}`);
    }
    
    if (profile.coreProfile?.challenges && profile.coreProfile.challenges.length > 0) {
      parts.push(`**Challenges:** ${profile.coreProfile.challenges.slice(0, 2).join(', ')}`);
    }
    
    if (profile.coreProfile?.interests && profile.coreProfile.interests.length > 0) {
      parts.push(`**Interests:** ${profile.coreProfile.interests.slice(0, 3).join(', ')}`);
    }

    if (profile.activeContext?.information) {
      parts.push(`\n${profile.activeContext.information}`);
    }

    // If no structured data, indicate we have raw Q&A
    if (parts.length === 0 && profile.onboardingAnswers?.answers?.length > 0) {
      return `We've received your onboarding responses (${profile.onboardingAnswers.answers.length} questions). Our AI is analyzing them to create your personalized experience!`;
    }

    return parts.length > 0 
      ? parts.join('\n\n')
      : "We've imported your profile from your previous onboarding.";
  }

  /**
   * Check if profile needs AI extraction of structured data
   * Returns true if we have Q&A but no structured data extracted yet
   */
  static needsAIExtraction(profile: PrivateProfile): boolean {
    const hasQA = (profile.onboardingAnswers?.answers?.length || 0) > 0;
    const hasStructuredData = (
      (profile.coreProfile?.goals?.length || 0) > 0 ||
      (profile.coreProfile?.interests?.length || 0) > 0 ||
      (profile.coreProfile?.challenges?.length || 0) > 0
    );
    
    return hasQA && !hasStructuredData;
  }
}

